import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import "./Department.css";

const url = process.env.REACT_APP_BACKEND_URL;

const DepartmentForm = () => {
  const [name, setName] = useState("");
  const [district, setDistrict] = useState("");
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  // Fetch all active districts
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const res = await axios.get(`${url}/api/district`);
        setDistricts(res.data.data || []);
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to fetch districts");
      } finally {
        setFetching(false);
      }
    };
    fetchDistricts();
  }, []);

  // If editing, fetch department data
  useEffect(() => {
    if (isEdit) {
      const fetchDepartment = async () => {
        try {
          const res = await axios.get(`${url}/api/department/${id}`);
          const department = res.data.data;
          setName(department.name);
          setDistrict(department.district?._id || "");
        } catch (err) {
          toast.error(err.response?.data?.message || "Failed to fetch department");
          navigate("/departments");
        }
      };
      fetchDepartment();
    }
  }, [id, isEdit, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error("Department name is required");
      return;
    }

    if (!district) {
      toast.error("Please select a district");
      return;
    }

    setLoading(true);

    try {
      const departmentData = { 
        name: name.trim(), 
        district 
      };

      if (isEdit) {
        await axios.put(`${url}/api/department/${id}`, departmentData);
        toast.success("Department updated successfully!");
      } else {
        await axios.post(`${url}/api/department`, departmentData);
        toast.success("Department added successfully!");
      }

      navigate("/departments");
    } catch (err) {
      toast.error(err.response?.data?.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  if (fetching && isEdit) {
    return <div className="loading">Loading department data...</div>;
  }

  return (
    <div className="department-container">
      <div className="header">
        <div className="header-left">
          <button onClick={() => navigate(-1)} className="back-button">
            Back
          </button>
          <h2>{isEdit ? "Edit Department" : "Add New Department"}</h2>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="department-form">
        <div className="form-group">
          <label>Department Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter department name"
            required
          />
        </div>

        <div className="form-group">
          <label>District</label>
          <select
            value={district}
            onChange={(e) => setDistrict(e.target.value)}
            required
          >
            <option value="">Select District</option>
            {districts.map((d) => (
              <option key={d._id} value={d._id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-actions">
          <button type="submit" disabled={loading} className="submit-button">
            {loading ? "Processing..." : isEdit ? "Update Department" : "Save Department"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/departments")}
            className="cancel-button"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default DepartmentForm;